"use client"

import { ColumnDef, RowData } from "@tanstack/react-table"
import { Check, MoreHorizontal } from "lucide-react"
import { Checkbox } from "@/Components/ui/checkbox"
import { Button } from "@/Components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu"
import { useContext } from "react"
import { AppContext } from "@/App"
import { AcknowledgedIcon, DashboardIcon, SeenIcon, StatusReportIcon } from "@/Components/ui/Icons/TooltipIcon"
import { PageLockContext } from "@/Components/PageLockProvider"
import { WorkPackageCell } from "@/Components/datatable/cells/workpackage_cell"
import { TextCell } from "@/Components/datatable/cells/text_cell"
import { OwnerCell } from "@/Components/datatable/cells/owner_cell"
import { DateCell } from "@/Components/datatable/cells/date_cell"
import { DueDateCell } from "@/Components/datatable/cells/duedate_cell"
import { RatingCell } from "@/Components/datatable/cells/rating_cell"
import { SupportRequestTypeCell } from "@/Components/datatable/cells/support_request_type_cell"
import { StatusCell } from "@/Components/datatable/cells/status_cell"
import { BoolCell } from "@/Components/datatable/cells/bool_cell"
import { RaisedByCell } from "@/Components/datatable/cells/raisedby_cell"
import { SupportRequest } from "@/Components/SupportRequests/SupportRequest"

declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        filterVariant?: 'text' | 'date-range' | 'select' | 'bool-select'
    }
}

export function getSupportRequestColumns(HandleDeleteClicked: (ids: number[]) => void, HandleEditClicked: (id: number) => void): ColumnDef<SupportRequest>[] {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);
    
    const columns: ColumnDef<SupportRequest>[] = [];

    if (app.authLevel !== 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        checked={
                            table.getIsAllPageRowsSelected() ||
                            (table.getIsSomePageRowsSelected() && "indeterminate")
                        }
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label="Select all"
                        id="table-select-all"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        onClick={(e) => e.stopPropagation()}
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Select row"
                        className="table-row-checkbox"
                    />
                ),
                enableSorting: false,
                enableHiding: false,
            }
        );
    }

    columns.push(
        {
            id: "supportRequestId",
            accessorKey: "supportRequestId",
            header: "ID",
            cell: ({ row }) => {
                const supportRequestId = row.getValue("supportRequestId");
                return <div className="text-center">{supportRequestId as number}</div>
            },
            filterFn: (row, columnId, filterValue) => {
                // needs to match exact number
                const value = row.getValue(columnId) as number;
                const filterNumber = typeof filterValue === "string" ? parseInt(filterValue, 10) : filterValue;
                return value === filterNumber;
            },
            size: 50,
        });

    if (app.selectedProject?.projectTypeId === 2) {
        columns.push({
            id: "workstream",
            accessorKey: "workstream",
            header: "WS",
            meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const workstream = row.getValue("workstream");
                return <div className="text-center">{workstream as string}</div>
            },
            size: 120,
        });
    }

    columns.push(
        {
            id: "workPackage",
            accessorKey: "workPackage",
            header: "WP",
            meta: {
                filterVariant: 'select',
            },
            cell: WorkPackageCell,
            size: 120,
        },
        {
            id: "description",
            accessorKey: "description",
            header: "Request",
            cell: TextCell,
            size: 400,
        },
        {
            id: "raisedBy",
            accessorKey: "raisedBy",
            header: "Raised By",
            cell: RaisedByCell,
            size: 110,
        },
        {
            id: "date",
            accessorKey: "date",
            header: "Date Raised",
            meta: {
                filterVariant: 'date-range',
            },
            cell: DateCell,
            filterFn: (row, columnId, filterValue) => {
                const value = new Date(row.getValue(columnId));
                const from = filterValue.from ? new Date(filterValue.from) : null;
                const to = filterValue.to ? new Date(filterValue.to) : null;
                if (!from || !to) return true;
                return value >= from && value <= to;
            },
            size: 110,
        },
        {
            id: "owner",
            accessorKey: "owner",
            header: "Owner",
            cell: OwnerCell,
            size: 110,
        },
        {
            id: "dueDate",
            accessorKey: "dueDate",
            header: "Due Date",
            meta: {
                filterVariant: 'date-range',
            },
            cell: DueDateCell,
            filterFn: (row, columnId, filterValue) => {
                const value = new Date(row.getValue(columnId));
                const from = filterValue.from ? new Date(filterValue.from) : null;
                const to = filterValue.to ? new Date(filterValue.to) : null;
                if (!from || !to) return true;
                return value >= from && value <= to;
            },
            size: 110,
        },
        {
            id: "requestType",
            accessorKey: "requestType",
            header: "Request Type",
            meta: {
                filterVariant: 'select',
            },
            cell: SupportRequestTypeCell,
            size: 110,
        },
        {
            id: "rating",
            accessorKey: "rating",
            header: "Rating",
            meta: {
                filterVariant: 'select',
            },
            cell: RatingCell,
            size: 110,
        },
        {
            id: "status",
            accessorKey: "status",
            header: "Status",
            meta: {
                filterVariant: 'select',
            },
            cell: StatusCell,
            size: 110,
        },
        {
            id: "notes",
            accessorKey: "notes",
            header: "Notes",
            cell: TextCell,
            size: 110,
        },
        {
            id: "displayOnStatusReport",
            accessorKey: "displayOnStatusReport",
            header: () => {
                return (<StatusReportIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: BoolCell,
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        },
        {
            id: "displayOnDashboard",
            accessorKey: "displayOnDashboard",
            header: () => {
                return (<DashboardIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: BoolCell,
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        },
        {
            id: "seen",
            accessorKey: "seen",
            header: () => {
                return (<SeenIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const seen = row.getValue("seen");
                return <>{seen ? <Check className="m-auto" /> : ""}</>
            },
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        },
        {
            id: "acknowledged",
            accessorKey: "acknowledged",
            header: () => {
                return (<AcknowledgedIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const acknowledged = row.getValue("acknowledged");
                return <>{acknowledged ? <Check className="m-auto" /> : ""}</>
            },
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        }
    );

    if (app.authLevel !== 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "actions",
                cell: ({ row }) => {
                    const SupportRequest = row.original

                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 table-row-menu-button">
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                {app.authLevel > 3 && !app.applicationUser?.userSettings?.inlineTableEditing && (
                                    <DropdownMenuItem
                                        onClick={(e) => {e.stopPropagation(); HandleEditClicked(SupportRequest.supportRequestId)}}
                                    >
                                        Edit
                                    </DropdownMenuItem>
                                )}
                                {app.authLevel > 3 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={(e) => {e.stopPropagation(); HandleDeleteClicked([SupportRequest.supportRequestId])}}
                                        >
                                            Delete
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )
                },
            }
        );
    }

    return columns
}

export const columnDisplayNames: [string, string][] = [
    ["supportRequestId", "ID"],
    ["workstream", "Workstream"],
    ["workPackage", "Work Package"],
    ["description", "Request"],
    ["raisedBy", "Raised By"],
    ["owner", "Owner"],
    ["date", "Date Raised"],
    ["dueDate", "Due Date"],
    ["rating", "Rating"],
    ["requestType", "Request Type"],
    ["status", "Status"],
    ["notes", "Notes"],
    ["displayOnStatusReport", "Display on Status Report"],
    ["displayOnDashboard", "Display on Dashboard"],
    ["seen", "Seen"],
    ["acknowledged", "Acknowledged"],
]